import React, { Component } from 'react'
import { func, bool } from 'prop-types'
import classNames from 'classnames'

import FurnitureItem from 'Renderer/FurnitureItem'
import { convertMeterDimensionsByLocale } from 'utils/convertDimensions'
import { workflow } from 'utils/tracker'

import { furnitureItemType } from 'models/propTypes'
import SugarcubeContext from 'apps/SugarcubeContext'
import { withTranslation } from 'react-i18next'
import Tooltip from 'components/Tooltip'
import ActionPanelContainer from 'containers/design/ActionPanelContainer'
import CompositionItemDraggable from 'components/CompositionItemDraggable'
import { LOCALES_USING_IMPERIAL_SYSTEM } from 'models/location/constants'
import { getProductTrackingInfo } from 'models/tracking/utils'
import { getRenderer } from 'models/refStore/selectors'

require('./CompositionFurnitureItem.scss')

const CM_SUFFIX = 'cm'
const INCHES_SUFFIX = '"'

class CompositionFurnitureItem extends Component {
    static optionsMD(e) {
        // Stop the mouseDown if we're clicking
        // on options so that the dragStart doesn't
        // interfere
        e.stopPropagation()
    }

    constructor(props) {
        super(props)
        this.onRender = this.onRender.bind(this)
        this.updateFromRenderObject = this.updateFromRenderObject.bind(this)
        this.RenderObject = null
        this.aoeRef = React.createRef()

        this.onDragStart = this.onDragStart.bind(this)
        this.onDrag = this.onDrag.bind(this)
        this.onDragFinish = this.onDragFinish.bind(this)

        this.onHoverStart = this.onHoverStart.bind(this)
        this.onHoverEnd = this.onHoverEnd.bind(this)

        this.toggleRotate = this.toggleRotate.bind(this)
        this.enableRotate = this.enableRotate.bind(this)
        this.disableRotate = this.disableRotate.bind(this)
        this.deleteRenderItem = this.deleteRenderItem.bind(this)
        this.makeDimensionsLabel = this.makeDimensionsLabel.bind(this)
        this.getRenderObject = this.getRenderObject.bind(this)

        const {
            renderItem: {
                max: { x: maxX, y: maxY, z: maxZ },
                min: { x: minX, y: minY, z: minZ },
                scale,
                region,
            },
        } = props

        this.dimensions = {
            x: convertMeterDimensionsByLocale((maxX - minX) * scale, region),
            y: convertMeterDimensionsByLocale((maxY - minY) * scale, region),
            z: convertMeterDimensionsByLocale((maxZ - minZ) * scale, region),
        }

        this.state = {
            corners: null,
            tagPoint: { x: Infinity, y: Infinity },
            dragging: false,
            rotating: false,
            shiftkeyPressed: false,
            loaded: false,
            // Allow components to respond to item teleporting
            needsUpdatedCorners: true,
        }
    }

    componentDidMount() {
        const { renderItem } = this.props
        const renderer = getRenderer()
        this.RenderObject = new FurnitureItem(
            renderItem.uniqueId,
            renderer,
            [renderItem.x, renderItem.y],
            this.onRender,
            renderItem,
            this.updateFromRenderObject,
        )
        if (renderItem.selected) {
            this.RenderObject.onSelect()
        }

        window.addEventListener('keydown', this.enableRotate)
        window.addEventListener('keyup', this.disableRotate)
        window.addEventListener('keydown', this.deleteRenderItem)
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.dragging !== this.state.dragging) {
            this.RenderObject.setSelected(!this.state.dragging)
        }

        const prevPlaneId = prevProps.renderItem.stackedPlane
        const newPlaneId = this.props.renderItem.stackedPlane
        if (newPlaneId !== prevPlaneId) {
            const { x, y } = this.props.renderItem
            this.RenderObject.setPlane(newPlaneId, [x, y], true)
        }

        const { x: prevX, y: prevY } = prevProps.renderItem
        const { x, y } = this.props.renderItem
        if (x !== prevX || y !== prevY) {
            // Track that we're updating the position externally (item teleports)
            // then set the position
            this.setState({ needsUpdatedCorners: true }, () => {
                this.RenderObject.setPosition([x, y])
            })
        }

        const {
            renderItem: { selected },
        } = this.props
        const {
            renderItem: { selected: wasSelected },
        } = prevProps

        const isSwappingSurfaces = this.props.isSwappingSurfaces
        const prevSwappingSurfaces = prevProps.isSwappingSurfaces

        if (selected && isSwappingSurfaces !== prevSwappingSurfaces) {
            this.RenderObject.setVisible(!isSwappingSurfaces)
        }

        if (selected && !wasSelected) {
            this.RenderObject.onSelect()
        } else if (!selected && wasSelected) {
            this.RenderObject.onSelectEnd()
        }
    }

    componentWillUnmount() {
        if (this.RenderObject) {
            if (this.props.renderItem.selected) this.RenderObject.onSelectEnd()

            this.RenderObject.endIsNigh()
            this.RenderObject = null
        }
        window.removeEventListener('keydown', this.enableRotate)
        window.removeEventListener('keyup', this.disableRotate)
        window.removeEventListener('keydown', this.deleteRenderItem)
    }

    onRender({ corners, metrics, tagPoint }) {
        if (corners || tagPoint) {
            // TODO: investigate this condition need
            this.setState({ corners, tagPoint })

            // If we were waiting for corners, reset the state
            if (this.state.needsUpdatedCorners) {
                this.setState({ needsUpdatedCorners: false })
            }
        }
        if (metrics) {
            const { isInitialLoading, renderItem } = this.props
            const productTrackingInfo = getProductTrackingInfo(renderItem)
            workflow('DecorateRoom.AddProductLoadTime', {
                ...metrics,
                ...productTrackingInfo,
                LoadTime: metrics.sTotalTime,
                CompositionLoading: isInitialLoading,
            })
            this.setState({ loaded: true })
            this.props.onLoad(metrics)
        }
    }

    onDragStart(rotating = false) {
        const { renderItem, doShowPlaceableSurfaces, onDragStart } = this.props

        if (onDragStart) {
            onDragStart()
        }
        if (renderItem && renderItem.stackedPlane !== null) {
            doShowPlaceableSurfaces([renderItem.stackedPlane])
        }
        this.RenderObject.onDragStart()
        this.setState({ dragging: true, rotating })

        this.props.clearSwapAffinity()
    }

    onDrag(move) {
        const { x, y, heading, overrideHeading } = move

        if (this.state.rotating || this.state.shiftkeyPressed || overrideHeading) {
            this.RenderObject.setOrientation(heading)
        } else {
            const options = { enforceBounds: false }

            this.RenderObject.setPosition([x, y], options)

            this.props.onDrag({ isValid: true })
        }
    }

    onDragFinish() {
        const { renderItem, doHidePlaceableSurfaces, onDragFinish } = this.props

        if (onDragFinish) {
            onDragFinish()
        }

        const [x, y] = this.RenderObject.getPosition()
        const heading = this.RenderObject.getOrientation()

        const nextPosition = {}

        if (x !== renderItem.x || y !== renderItem.y) {
            nextPosition.x = x
            nextPosition.y = y
        }

        if (heading !== renderItem.heading) {
            nextPosition.heading = heading
        }

        if (Object.keys(nextPosition).length && this.props.update) {
            this.props.update(nextPosition)
        }

        this.RenderObject.onDragFinish()
        doHidePlaceableSurfaces()
        this.setState({ dragging: false, rotating: false })

        return nextPosition
    }

    onHoverStart() {
        const { isMagicWindow, isKiosk } = this.context
        if (isMagicWindow || isKiosk) {
            return
        }
        this.RenderObject.onHover()
    }

    onHoverEnd() {
        const { isMagicWindow, isKiosk } = this.context
        if (isMagicWindow || isKiosk) {
            return
        }
        this.RenderObject.onHoverEnd()
    }

    getRenderObject() {
        return this.RenderObject
    }

    updateFromRenderObject(x, y) {
        const { renderItem, update } = this.props
        update({ x, y, heading: renderItem.heading })
    }

    toggleRotate() {
        this.setState((prevState) => ({ rotating: !prevState.rotating }))
    }

    enableRotate(e) {
        if (e.keyCode === 16 && !this.state.shiftkeyPressed) {
            this.setState({ shiftkeyPressed: true })
        }
    }

    disableRotate(e) {
        if (e.keyCode === 16) {
            this.setState({ shiftkeyPressed: false })
        }
    }

    deleteRenderItem(e) {
        const {
            delete: del,
            renderItem: { selected },
        } = this.props
        if (selected && (e.keyCode === 8 || e.keyCode === 46)) {
            e.stopPropagation()
            e.preventDefault()
            del()
        }
    }

    makeDimensionsLabel() {
        const {
            renderItem: { region },
        } = this.props
        const { x, y, z } = this.dimensions
        const dimensionSymbol = LOCALES_USING_IMPERIAL_SYSTEM.includes(region) ? INCHES_SUFFIX : CM_SUFFIX
        return `L ${x}${dimensionSymbol}, W ${z}${dimensionSymbol}, H ${y}${dimensionSymbol}`
    }

    render() {
        const {
            delete: del,
            openSettings,
            isUnderCursor,
            renderItem,
            isCurrentDragValid,
            onSelect,
            t,
            disableDrag,
            onStartSwapStackableSurface,
            revealDots,
            showDot,
            isSwappingSurfaces,
            isDraggingRenderItem,
            duplicate,
        } = this.props

        const {
            x,
            y,
            heading,
            selected,
            uniqueId,
            name,
            price,
            description,
            isLeftMost,
            stacks,
            localItemNumber,
            region,
            isCombinableChild,
        } = renderItem
        const { rotating, shiftkeyPressed, corners, dragging, loaded, needsUpdatedCorners } = this.state

        const isInRotatingMode = rotating || shiftkeyPressed
        const dimensionsLabel = this.makeDimensionsLabel()

        const trackingInfo = getProductTrackingInfo(renderItem)

        const renderer = getRenderer()
        if (!renderer) {
            return null
        }

        const { isMobile, isKiosk } = this.context
        return (
            <>
                {!disableDrag && !isCombinableChild && (
                    <CompositionItemDraggable
                        className={classNames('CompositionFurnitureItem', { selected, dragging })}
                        isUnderCursor={isUnderCursor}
                        corners={corners}
                        renderDimensions={renderer.getRenderDimensions()}
                        onDragStart={this.onDragStart}
                        onDrag={this.onDrag}
                        onDragFinish={this.onDragFinish}
                        onClick={onSelect}
                        x={x}
                        y={y}
                        rotating={isInRotatingMode}
                        isCurrentDragValid={isCurrentDragValid}
                        selected={selected}
                        name={name}
                        price={price}
                        description={description}
                        openSettings={openSettings}
                        revealDots={revealDots}
                        getRenderObject={this.getRenderObject}
                        showDot={showDot}
                        tagPoint={this.state.tagPoint}
                    >
                        {isKiosk ? null : (
                            <div
                                className={classNames('aoe', { isLeftMost })}
                                data-tip={t('tooltip.click to access more options')}
                                data-for={`ttclick${uniqueId}`}
                                data-tip-disable={selected}
                                data-event-off="click"
                                onMouseEnter={this.onHoverStart}
                                onMouseLeave={this.onHoverEnd}
                                ref={this.aoeRef}
                            >
                                <Tooltip id={`ttclick${uniqueId}`} hide={selected} />
                                {this.dimensions ? (
                                    <div className="dimensionsContainer">
                                        <div className="dimensions">
                                            <span>{dimensionsLabel}</span>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </CompositionItemDraggable>
                )}
                {!isMobile &&
                    selected &&
                    loaded &&
                    !isSwappingSurfaces &&
                    // TODO: Shouldn't need rotating in the corners check, but we run into a precision
                    // issue in the check above which sets the needsUpdatedCorners state (x/y shouldn't change
                    // when rotating)
                    (!needsUpdatedCorners || rotating) &&
                    (!isDraggingRenderItem || rotating) && (
                        <ActionPanelContainer
                            targetRef={this.aoeRef}
                            heading={heading}
                            onDelete={del}
                            onRotateStart={() => this.onDragStart(true)}
                            onRotate={(h) => this.onDrag({ heading: h, overrideHeading: true })}
                            onRotateFinish={this.onDragFinish}
                            onDuplicate={duplicate}
                            onChangeSurface={stacks ? onStartSwapStackableSurface : null}
                            localItemNumber={localItemNumber}
                            region={region}
                            trackingInfo={trackingInfo}
                            name={name}
                        />
                    )}
            </>
        )
    }
}

CompositionFurnitureItem.contextType = SugarcubeContext
CompositionFurnitureItem.defaultProps = {
    delete: null,
    openSettings: null,
    disableDrag: false,
    onDrag: null,
    onDragStart: null,
    onDragFinish: null,
    onSelect: null,
}

CompositionFurnitureItem.propTypes = {
    renderItem: furnitureItemType.isRequired,
    isUnderCursor: bool.isRequired,

    delete: func,
    openSettings: func,
    update: func.isRequired,
    clearSwapAffinity: func.isRequired,
    duplicate: func.isRequired,

    onDragStart: func,
    onDragFinish: func,
    onDrag: func,
    onLoad: func.isRequired,
    onSelect: func,
    onStartSwapStackableSurface: func.isRequired,
    isCurrentDragValid: bool.isRequired,
    t: func.isRequired,
    doShowPlaceableSurfaces: func.isRequired,
    doHidePlaceableSurfaces: func.isRequired,

    disableDrag: bool,
    isSwappingSurfaces: bool.isRequired,
    isDraggingRenderItem: bool.isRequired,
    isInitialLoading: bool.isRequired,
    showDot: bool.isRequired,
    revealDots: bool.isRequired,
}

export default withTranslation()(CompositionFurnitureItem)
